"use client";

import { config } from "@repo/config";
import { isLocaleAvailableForPath } from "@shared/lib/seo";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

export function AutoLocaleSetter() {
	const currentLocale = useLocale();
	const pathname = usePathname();
	// 按路径去重，而不是"整个会话只跑一次"：部分翻译的语言在未翻译页面上会跳过
	// 跳转且刻意不写 localStorage，用户之后导航到已翻译页面时必须能再判断一次。
	// 这个组件挂在 layout 上、客户端导航时不会卸载，所以用布尔值会让那次
	// "再判断" 永远不发生。
	const checkedPaths = useRef(new Set<string>());
	// 地理检测结果缓存：改成按路径重跑之后，不缓存会导致每次导航都打一次
	// /api/detect-locale。
	const geoLocale = useRef<string | null | undefined>(undefined);

	useEffect(() => {
		// 跳过搜索引擎爬虫，避免干扰 Googlebot 抓取
		if (
			navigator.webdriver ||
			/bot|crawl|spider|slurp|googlebot|bingbot|yandex/i.test(
				navigator.userAgent,
			)
		) {
			return;
		}

		// 浏览器禁用站点数据时，连读 window.localStorage 这个属性都会抛
		// SecurityError，而这里在 effect 里，抛出去就是整页错误边界（10/31–11/02
		// 有 10 个会话一落地就崩）。拿不到存储就不自动跳：防来回跳的标记写不进去，
		// 照跳会在两个语种之间无限循环。
		let storage: Storage;
		try {
			storage = window.localStorage;
		} catch {
			return;
		}

		// 检查是否已经自动设置过语言
		const autoLocaleSet = storage.getItem("auto-locale-set");
		if (autoLocaleSet || checkedPaths.current.has(pathname)) {
			return;
		}

		checkedPaths.current.add(pathname);

		// 上次的跳转已经落地，检测到此结束。标记只在落地后才写：原先跳转前就写，
		// 跳转一旦被打断（比如等待期间用户点了登录、请求失败），这台设备就
		// 再也不检测了——10/6 那位 zh-CN 付费用户就是标记写上了、却一次也没进过
		// /zh 页。也不能干脆不写标记：同时偏好 ja 和 zh 的浏览器会在两边来回跳。
		if (storage.getItem("auto-locale-pending") === currentLocale) {
			storage.setItem("auto-locale-set", "true");
			storage.removeItem("auto-locale-pending");
			return;
		}

		async function detectAndSetPreferredLocale() {
			const locale = currentLocale;

			// 检查浏览器语言偏好
			const browserLanguages = navigator.languages || [
				navigator.language,
			];

			const hasJapanesePreference = browserLanguages.some(
				(lang) =>
					lang.toLowerCase().includes("ja") ||
					lang.toLowerCase().includes("jp"),
			);

			const hasTraditionalChinesePreference = browserLanguages.some(
				(lang) =>
					lang.toLowerCase().includes("zh-tw") ||
					lang.toLowerCase().includes("zh-hk") ||
					lang.toLowerCase().includes("zh-mo"),
			);

			const hasSimplifiedChinesePreference = browserLanguages.some(
				(lang) =>
					lang.toLowerCase().includes("zh-cn") ||
					lang.toLowerCase().includes("zh-sg"),
			);

			const hasChinesePreference = browserLanguages.some(
				(lang) =>
					lang.toLowerCase().includes("zh") ||
					lang.toLowerCase().includes("cn"),
			);

			const hasPortuguesePreference = browserLanguages.some((lang) =>
				lang.toLowerCase().startsWith("pt"),
			);

			const hasSpanishPreference = browserLanguages.some((lang) =>
				lang.toLowerCase().startsWith("es"),
			);

			// 优先级：日语 > 繁体中文 > 简体中文 > 巴葡 > 西语
			let suggestedLocale: string | null = null;

			if (hasJapanesePreference && locale !== "ja") {
				suggestedLocale = "ja";
			} else if (hasTraditionalChinesePreference && locale !== "zh-TW") {
				suggestedLocale = "zh-TW";
			} else if (hasSimplifiedChinesePreference && locale !== "zh") {
				suggestedLocale = "zh";
			} else if (
				hasChinesePreference &&
				locale !== "zh" &&
				locale !== "zh-TW"
			) {
				suggestedLocale = "zh";
			} else if (hasPortuguesePreference && locale !== "pt-BR") {
				suggestedLocale = "pt-BR";
			} else if (hasSpanishPreference && locale !== "es") {
				suggestedLocale = "es";
			}

			// 如果浏览器检测没有结果，则使用地理位置检测
			if (!suggestedLocale) {
				if (geoLocale.current === undefined) {
					try {
						const response = await fetch("/api/detect-locale");
						const geoResult = await response.json();
						geoLocale.current = geoResult.locale ?? null;
					} catch (error) {
						console.warn("Geo-detection failed:", error);
						geoLocale.current = null;
					}
				}

				const detected = geoLocale.current;

				if (detected === "ja" && locale !== "ja") {
					suggestedLocale = "ja";
				} else if (detected === "zh-TW" && locale !== "zh-TW") {
					suggestedLocale = "zh-TW";
				} else if (detected === "zh" && locale !== "zh") {
					suggestedLocale = "zh";
				} else if (detected === "pt-BR" && locale !== "pt-BR") {
					suggestedLocale = "pt-BR";
				} else if (detected === "es" && locale !== "es") {
					suggestedLocale = "es";
				}
			}

			// 跳转前先算出去掉语言前缀的路径
			const pathWithoutLocale = pathname.replace(
				/^\/[a-z]{2}(-[A-Z]{2})?(\/|$)/,
				"/",
			);

			// 部分翻译的语言（如 pt-BR）只在已翻译的页面上跳转，否则用户会被
			// 送到只有英文兜底内容的 URL。这里不写 localStorage 标记，
			// 用户之后打开已翻译的页面时还能再判断一次（靠上面的按路径去重）。
			if (
				suggestedLocale &&
				!isLocaleAvailableForPath(suggestedLocale, pathWithoutLocale)
			) {
				return;
			}

			// 如果检测到建议的语言，自动设置
			if (suggestedLocale) {
				// 同步写 cookie 后立刻跳。原先要先等 updateLocale 这个 server action
				// 往返（还会顺带 revalidatePath("/")），白白拉长了被打断的窗口
				const localeCookie = `${config.i18n.localeCookieName}=${suggestedLocale}`;
				document.cookie = `${localeCookie}; path=/; max-age=31536000; samesite=lax`;

				// saas 页按 cookie 定语种（/zh/subscription 会被弹回 /subscription），
				// cookie 写不进去就永远落不了地，只能放弃，否则会无限跳
				if (!document.cookie.split("; ").includes(localeCookie)) {
					storage.setItem("auto-locale-set", "true");
					return;
				}

				storage.setItem("auto-locale-pending", suggestedLocale);

				// 跳转到对应语言版本
				const newPath =
					suggestedLocale === config.i18n.defaultLocale
						? pathWithoutLocale
						: `/${suggestedLocale}${pathWithoutLocale}`;

				window.location.href = newPath;
			} else {
				// 即使没有检测到，也标记为已处理，避免重复检测
				storage.setItem("auto-locale-set", "true");
			}
		}

		detectAndSetPreferredLocale();
	}, [currentLocale, pathname]);

	return null;
}
